"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DsnCfbBinarySpec = void 0;
var DsnCfbBinarySpec;
(function (DsnCfbBinarySpec) {
    function readUInt16LE(bytes, offset) {
        return bytes[offset] | (bytes[offset + 1] << 8);
    }
    DsnCfbBinarySpec.readUInt16LE = readUInt16LE;
    function readInt16LE(bytes, offset) {
        const value = readUInt16LE(bytes, offset);
        return value >= 0x8000 ? value - 0x10000 : value;
    }
    DsnCfbBinarySpec.readInt16LE = readInt16LE;
    function readUInt32LE(bytes, offset) {
        return ((bytes[offset] | (bytes[offset + 1] << 8) | (bytes[offset + 2] << 16) | (bytes[offset + 3] * 0x1000000)) >>> 0);
    }
    DsnCfbBinarySpec.readUInt32LE = readUInt32LE;
    function readInt32LE(bytes, offset) {
        const value = readUInt32LE(bytes, offset);
        return value >= 0x80000000 ? value - 0x100000000 : value;
    }
    DsnCfbBinarySpec.readInt32LE = readInt32LE;
    function bytesToAscii(bytes, start, end) {
        let value = '';
        for (let i = start; i < end; i++) {
            value += String.fromCharCode(bytes[i]);
        }
        return value;
    }
    DsnCfbBinarySpec.bytesToAscii = bytesToAscii;
    function bytesToHex(bytes, start, end) {
        let value = '';
        for (let offset = start; offset < end; offset++) {
            value += bytes[offset].toString(16).padStart(2, '0');
        }
        return value;
    }
    DsnCfbBinarySpec.bytesToHex = bytesToHex;
    function isPrintableAsciiByte(byte) {
        return byte >= 0x20 && byte < 0x7f;
    }
    DsnCfbBinarySpec.isPrintableAsciiByte = isPrintableAsciiByte;
    function hasBytes(bytes, offset, expected) {
        if (offset < 0 || offset + expected.length > bytes.length) {
            return false;
        }
        return expected.every((byte, index) => bytes[offset + index] === byte);
    }
    DsnCfbBinarySpec.hasBytes = hasBytes;
    function resolveOffset(spec, ctx) {
        const offset = typeof spec === 'function' ? spec(ctx) : spec;
        return offset === undefined ? ctx.offset : ctx.offset + offset;
    }
    function resolveLength(spec, ctx) {
        return typeof spec === 'function' ? spec(ctx) : spec;
    }
    function fieldDiagnostic(ctx, specName, code, message, offset, byteLength) {
        return {
            severity: 'warning',
            code,
            message,
            specName,
            streamPath: ctx.streamPath,
            ...(offset !== undefined ? { offset } : {}),
            ...(byteLength !== undefined ? { byteLength } : {}),
        };
    }
    function readCountedAscii(bytes, offset, maxLength, requireNullTerminator) {
        if (offset + 2 > bytes.length) {
            return undefined;
        }
        const length = readUInt16LE(bytes, offset);
        const valueStart = offset + 2;
        const valueEnd = valueStart + length;
        if (length < 1 || length > maxLength || valueEnd > bytes.length) {
            return undefined;
        }
        for (let index = valueStart; index < valueEnd; index++) {
            if (!isPrintableAsciiByte(bytes[index])) {
                return undefined;
            }
        }
        const hasNullTerminator = valueEnd < bytes.length && bytes[valueEnd] === 0;
        if (requireNullTerminator && !hasNullTerminator) {
            return undefined;
        }
        return {
            value: bytesToAscii(bytes, valueStart, valueEnd),
            byteLength: 2 + length + (hasNullTerminator ? 1 : 0),
        };
    }
    DsnCfbBinarySpec.readCountedAscii = readCountedAscii;
    function readAsciiRuns(bytes, minLength, maxCount) {
        const runs = [];
        let start = -1;
        for (let offset = 0; offset <= bytes.length; offset++) {
            const byte = offset < bytes.length ? bytes[offset] : 0;
            if (offset < bytes.length && isPrintableAsciiByte(byte)) {
                if (start === -1) {
                    start = offset;
                }
                continue;
            }
            if (start !== -1) {
                const length = offset - start;
                if (length >= minLength) {
                    runs.push({ offset: start, length, value: bytesToAscii(bytes, start, offset) });
                    if (runs.length >= maxCount) {
                        return runs;
                    }
                }
                start = -1;
            }
        }
        return runs;
    }
    DsnCfbBinarySpec.readAsciiRuns = readAsciiRuns;
    function readField(name, field, ctx, specName) {
        if (field.when && !field.when(ctx)) {
            return undefined;
        }
        const diagnostics = [];
        const offset = resolveOffset(field.offset, ctx);
        if (offset === undefined || offset < 0 || offset > ctx.bytes.length) {
            if (field.optional) {
                return undefined;
            }
            diagnostics.push(fieldDiagnostic(ctx, specName, 'field-offset-out-of-range', `Field ${name} offset is outside the payload`, offset));
            return { value: undefined, ranges: [], diagnostics };
        }
        const requireBytes = (byteLength) => {
            if (offset + byteLength <= ctx.bytes.length) {
                return true;
            }
            diagnostics.push(fieldDiagnostic(ctx, specName, 'field-out-of-range', `Field ${name} extends past the payload`, offset, byteLength));
            return false;
        };
        const requiredFieldFailure = () => (field.optional ? undefined : { value: undefined, ranges: [], diagnostics });
        const range = (byteLength) => [{ offset, byteLength, reason: name }];
        switch (field.kind) {
            case 'u8':
                return requireBytes(1) ? { value: ctx.bytes[offset], ranges: range(1), diagnostics } : requiredFieldFailure();
            case 'u16le':
                return requireBytes(2) ?
                    { value: readUInt16LE(ctx.bytes, offset), ranges: range(2), diagnostics }
                    : requiredFieldFailure();
            case 'i16le':
                return requireBytes(2) ?
                    { value: readInt16LE(ctx.bytes, offset), ranges: range(2), diagnostics }
                    : requiredFieldFailure();
            case 'u32le':
                return requireBytes(4) ?
                    { value: readUInt32LE(ctx.bytes, offset), ranges: range(4), diagnostics }
                    : requiredFieldFailure();
            case 'i32le':
                return requireBytes(4) ?
                    { value: readInt32LE(ctx.bytes, offset), ranges: range(4), diagnostics }
                    : requiredFieldFailure();
            case 'bytes': {
                const byteLength = resolveLength(field.length, ctx);
                if (byteLength === undefined || !requireBytes(byteLength)) {
                    return requiredFieldFailure();
                }
                return { value: ctx.bytes.subarray(offset, offset + byteLength), ranges: range(byteLength), diagnostics };
            }
            case 'ascii': {
                const byteLength = resolveLength(field.length, ctx);
                if (byteLength === undefined || !requireBytes(byteLength)) {
                    return requiredFieldFailure();
                }
                return { value: bytesToAscii(ctx.bytes, offset, offset + byteLength), ranges: range(byteLength), diagnostics };
            }
            case 'fixed-bytes':
                if (!requireBytes(field.bytes.length) || !hasBytes(ctx.bytes, offset, field.bytes)) {
                    if (field.optional) {
                        return undefined;
                    }
                    diagnostics.push(fieldDiagnostic(ctx, specName, 'fixed-bytes-mismatch', `Field ${name} does not match fixed bytes`, offset));
                    return { value: false, ranges: range(field.bytes.length), diagnostics };
                }
                return { value: true, ranges: range(field.bytes.length), diagnostics };
            case 'counted-ascii': {
                const counted = readCountedAscii(ctx.bytes, offset, field.maxLength ?? 512, field.requireNullTerminator ?? true);
                if (!counted) {
                    if (field.optional) {
                        return undefined;
                    }
                    diagnostics.push(fieldDiagnostic(ctx, specName, 'counted-ascii-invalid', `Field ${name} is not valid counted ASCII`, offset));
                    return { value: undefined, ranges: [], diagnostics };
                }
                return { value: counted.value, ranges: range(counted.byteLength), diagnostics };
            }
            case 'ascii-runs': {
                const scanByteLength = field.byteLength === undefined ? undefined : resolveLength(field.byteLength, ctx);
                const scanEnd = scanByteLength === undefined ? ctx.bytes.length : Math.min(ctx.bytes.length, offset + scanByteLength);
                const runs = readAsciiRuns(ctx.bytes.subarray(offset, scanEnd), field.minLength ?? 4, field.maxCount ?? 200).map((run) => ({
                    ...run,
                    offset: run.offset + offset,
                }));
                return {
                    value: runs,
                    ranges: runs.map((run) => ({ offset: run.offset, byteLength: run.length, reason: name })),
                    diagnostics,
                };
            }
            case 'repeated': {
                const count = typeof field.count === 'function' ? field.count(ctx) : field.count;
                const itemByteLength = resolveLength(field.byteLength, ctx);
                if (count === undefined || itemByteLength === undefined || count < 0 || !requireBytes(count * itemByteLength)) {
                    return requiredFieldFailure();
                }
                const values = [];
                const ranges = [];
                for (let index = 0; index < count; index++) {
                    const itemCtx = {
                        ...ctx,
                        offset: offset + index * itemByteLength,
                        fields: {},
                    };
                    const item = {};
                    for (const [itemFieldName, itemField] of Object.entries(field.fields)) {
                        const itemFieldResult = readField(itemFieldName, itemField, itemCtx, specName);
                        if (!itemFieldResult && !itemField.optional) {
                            return undefined;
                        }
                        if (itemFieldResult) {
                            item[itemFieldName] = itemFieldResult.value;
                            itemCtx.fields[itemFieldName] = itemFieldResult.value;
                            ranges.push(...itemFieldResult.ranges);
                            diagnostics.push(...itemFieldResult.diagnostics);
                        }
                    }
                    values.push(item);
                }
                return { value: values, ranges, diagnostics };
            }
            case 'custom': {
                const value = field.read({
                    ...ctx,
                    offset,
                });
                const byteLength = field.byteLength ? resolveLength(field.byteLength, ctx) : undefined;
                return {
                    value,
                    ranges: byteLength !== undefined ? range(byteLength) : [],
                    diagnostics,
                };
            }
            default:
                return undefined;
        }
    }
    function mergeRanges(ranges) {
        const sorted = ranges
            .filter((range) => range.byteLength > 0)
            .sort((left, right) => left.offset - right.offset || left.byteLength - right.byteLength);
        const merged = [];
        for (const range of sorted) {
            const previous = merged[merged.length - 1];
            if (previous && range.offset <= previous.offset + previous.byteLength) {
                const previousEnd = previous.offset + previous.byteLength;
                const rangeEnd = range.offset + range.byteLength;
                previous.byteLength = Math.max(previousEnd, rangeEnd) - previous.offset;
                previous.reason = previous.reason === range.reason ? previous.reason : 'known-fields';
                continue;
            }
            merged.push({ ...range });
        }
        return merged;
    }
    function unknownRangesFromKnownRanges(knownRanges, start, byteLength) {
        const unknownRanges = [];
        let cursor = start;
        for (const range of mergeRanges(knownRanges)) {
            if (range.offset > cursor) {
                unknownRanges.push({
                    offset: cursor,
                    byteLength: range.offset - cursor,
                    reason: 'unknown',
                });
            }
            cursor = Math.max(cursor, range.offset + range.byteLength);
        }
        const end = start + byteLength;
        if (cursor < end) {
            unknownRanges.push({
                offset: cursor,
                byteLength: end - cursor,
                reason: 'unknown',
            });
        }
        return unknownRanges;
    }
    function decodeRecordSpec(spec, ctx) {
        if (!spec.match.every((predicate) => predicate(ctx))) {
            return undefined;
        }
        const fieldContext = {
            ...ctx,
            fields: {},
        };
        const diagnostics = [];
        const knownRanges = [...(spec.knownRanges ?? [])];
        for (const [name, field] of Object.entries(spec.fields)) {
            const result = readField(name, field, fieldContext, spec.name);
            if (!result) {
                continue;
            }
            fieldContext.fields[name] = result.value;
            knownRanges.push(...result.ranges);
            diagnostics.push(...result.diagnostics);
        }
        if (spec.validate && !spec.validate.every((predicate) => predicate(fieldContext))) {
            return undefined;
        }
        const value = spec.decode ? spec.decode(fieldContext) : spec.derive?.(fieldContext.fields ?? {}, fieldContext);
        if (value === undefined) {
            return undefined;
        }
        const valueByteLength = (typeof value === 'object' &&
            value !== null &&
            'byteLength' in value &&
            typeof value.byteLength === 'number') ?
            value.byteLength
            : undefined;
        const spanOffset = ctx.payloadOffset ?? ctx.offset;
        const spanByteLength = ctx.byteLength ?? ctx.payloadByteLength ?? valueByteLength ?? ctx.bytes.byteLength - ctx.offset;
        const rangeOffsetDelta = spanOffset - ctx.offset;
        const unknownRanges = unknownRangesFromKnownRanges(knownRanges, ctx.offset, spanByteLength).map((range) => ({
            ...range,
            offset: range.offset + rangeOffsetDelta,
        }));
        return {
            kind: spec.kind,
            family: spec.family,
            value,
            span: {
                streamPath: ctx.streamPath,
                offset: spanOffset,
                byteLength: spanByteLength,
                ...(ctx.payloadOffset !== undefined ? { payloadOffset: ctx.payloadOffset } : {}),
                ...(ctx.payloadByteLength !== undefined ? { payloadByteLength: ctx.payloadByteLength } : {}),
            },
            specName: spec.name,
            confidence: spec.confidence ?? 'exact',
            diagnostics,
            ...(unknownRanges.length > 0 ? { unknownRanges } : {}),
        };
    }
    DsnCfbBinarySpec.decodeRecordSpec = decodeRecordSpec;
    function matchStreamSpecs(specs, entry) {
        return specs.filter((spec) => (spec.path instanceof RegExp ? spec.path.test(entry.path) : spec.path(entry)));
    }
    DsnCfbBinarySpec.matchStreamSpecs = matchStreamSpecs;
})(DsnCfbBinarySpec || (exports.DsnCfbBinarySpec = DsnCfbBinarySpec = {}));
//# sourceMappingURL=DsnCfbBinarySpec.js.map